// Ionic & Angular Imports
import {Component} from '@angular/core';
import {Events, NavParams, LoadingController, Loading, ModalController, AlertController, Refresher} from 'ionic-angular';

// Service Imports 
import {rsReportSvc, ReportState} from '../../../../services/report.service';
import {rsSubcontractorSvc, Subcontractor} from '../../../../services/subcontractor.service';
import {Company} from '../../../../services/company.service';

// Component Imports
import {BasePage} from '../../../base.component';
import {SubcontractorModal} from './subcontractor-modal/subcontractor-modal.component';

@Component({
    templateUrl: 'subcontractor.html',
    providers: [
        rsSubcontractorSvc
    ]
})
export class SubcontractorPage extends BasePage
{
    // Class variables 
    private contentList : Subcontractor[];
    private companyList : Company[];

    // Class Constructor
    constructor( 
        protected alertCtrl : AlertController, 
        private loadingCtrl : LoadingController,  
        private navParams : NavParams, 
        private eventSystem : Events,
        private modalCtrl : ModalController,
        private reportSvc: rsReportSvc, 
        private subcontractorSvc : rsSubcontractorSvc
    ){
        // Construct base class
        super(alertCtrl);

        interface NavData {
            companies : Company[];
            subcontractors : Subcontractor[];
        };

        // Retrieve the activity list from our NavParams       
        let navData : NavData = <NavData>this.navParams.get('content');

        console.log(navData);

        this.contentList = navData.subcontractors;
        this.companyList = navData.companies;
        this.sortContentList(); this.sortComapnyList();

        // Register our event handlers 
        this.registerEventHandlers( eventSystem );
    }

    // Class method used to subscribe to all events this class is interested in
    private registerEventHandlers( eventSystem : Events ) {
        eventSystem.subscribe( 'subcontractor:deleted', (data) => { this.deleteSubcontractor( data ); });
        eventSystem.subscribe( 'subcontractor:updated', (data) => { this.updateSubcontractor( data ); });
        eventSystem.subscribe( 'subcontractor:added',   (data) => { this.addSubcontractor( data ); });
    }

   // Sorting method for ensuring our ContentList is always sorted by id (ie., oldest to newest entries)
    private sortContentList(reverse : boolean = false) {
        this.contentList.sort( (a,b) => {
            return reverse ? b.id - a.id : a.id - b.id;
        });
    }
    private sortComapnyList(reverse : boolean = false ) {
        this.companyList.sort( (a, b) => {
            let nameA : string = (a.name).toLowerCase();
            let nameB : string = (b.name).toLowerCase();
            if( nameA < nameB ) return -1;
            if( nameA > nameB ) return 1;
            return 0;
        });
    }

    // Class method used to add a new labor to our ContentList 
    private addSubcontractor( subcontractor : Subcontractor ) : boolean {
        // Add this labor to the end of our ContentList  
        let length : number = this.contentList.push(subcontractor);

        // Finally, publish an event that our report has been updated
        this.eventSystem.publish( 'report:updated', ReportState.Subcontractor, length );      
        
        // Return successful
        return true;  
    }

    // Class method used to update an labor in our ContentList
    private updateSubcontractor( subcontractor : Subcontractor ) : boolean {
        // Loop through each item in the ContentList to attempt to find a matching labor
        for( var idx = 0; idx < this.contentList.length; idx++ ) {
            let item : Subcontractor = this.contentList[idx];
            if( item.id === subcontractor.id ) {
                // Update the content of this labor within the ContentList
                this.contentList[idx] = subcontractor;
                return true;          
            }
        }
        // No match was found, return false 
        return false;
    }

    // Class method used to delete an labor in our ContentList
    private deleteSubcontractor( subcontractor : Subcontractor ) : boolean {
        // Loop through each item in the ContentList to attempt to find a matching labor
        for( var idx = 0; idx < this.contentList.length; idx++ ) {
            let item : Subcontractor = this.contentList[idx];
            if( item.id === subcontractor.id ) {
                // Remove this labor from the ContentList
                this.contentList.splice( idx, 1 );

                // Publish an event that will update our report state 
                this.eventSystem.publish( 'report:updated', ReportState.Subcontractor, this.contentList.length );

                // Return successful
                return true;          
            }
        }
        // No match was found, return false 
        return false;
    }

    // Class method used by the template to edit an existing activity
    public updateContent( subcontractor : Subcontractor ) {
        // Create our ModalInstance and configure for editing
        this.modalCtrl.create( SubcontractorModal, { 
            subcontractor: subcontractor,
            companies: this.companyList,
            editmode: true
        }).present();
    }

    // Class method used by the template to create a new activity
    public createContent( ) {
        // Create our ModalInstance and configure for creating 
        this.modalCtrl.create( SubcontractorModal, { 
            subcontractor: null,
            companies: this.companyList,
            editmode: false
        }).present();      
    }

    // Class method used by the template to delete a subcontractor
    public deleteContent( subcontractor : Subcontractor ) {
        // Verify that we have a valid subcontractor id before trying to delete 
        if( subcontractor.id > 0 ) 
        {
            // Labor id is valid; instantiate delete confirmation
            this.displayConfirmation( 'Delete Subcontractor', 'Are you sure you want to delete "' + subcontractor.subcontractor.name + '" from this report?', () => 
            {
                // User confirmed- so let's initiate the delete process
                let loader : Loading = this.loadingCtrl.create( {
                    content: 'Deleting...'
                });
                loader.present().then( () => {
                    // Data hNow let's start our delete process 
                    this.subcontractorSvc.deleteSubcontractor( subcontractor.id, this.reportSvc.getActiveReportId() ).subscribe( result => {
                        // Data has been received from our service 
                        this.eventSystem.publish('subcontractor:deleted', subcontractor );
                    }, err => {
                        // Operation failed; dismiss loader and shown an error message 
                        loader.dismiss().then( () => {
                            this.displayError("There was an error deleting this subcontractor.  Please try again later.");
                        });
                    }, () => {
                        // Operation succeeded, dismiss loader
                        loader.dismiss();
                    });
                });
            });
        } else {
            // The subcontractor id supplied is not valid; display a generic error 
            this.displayError( 'An error has occurred.  Please try again later.' );
        }
    }

    // Class method used by the template to refresh the ContentList
    public refreshContent( refresher : Refresher ) {
        // Spawn a spinner while the ContentList is being refreshed
        let loader : Loading = this.loadingCtrl.create( {
            content: 'Refreshing...'
        });
        loader.present().then( () => {
            // The spinner is loaded, so let's get our refreshed ContentList
            this.subcontractorSvc.getSubcontractors( this.reportSvc.getActiveReportId() )
            .subscribe( result => {
                // Data was received from our service 
                this.contentList = result;
                this.sortContentList();

                // Publish an event that will update our report state 
                this.eventSystem.publish( 'report:updated', ReportState.Subcontractor, this.contentList.length );
            }, err => {
                // The operation failed, so let's handle this error 
                loader.dismiss().then( () => {
                    this.displayError( "There was an error refreshing the list.  Please try again later." ).then( () => {
                        refresher.complete();
                    });
                });
            }, () => {
                // The operation succeeded, let's dismiss our spinner and complete our refresh operation 
                loader.dismiss().then( () => {
                    refresher.complete();
                });
            });
        });
    }    
}


// WEBPACK FOOTER //
// ./src/pages/report/tab-main/subcontractor/subcontractor.component.ts